import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, InputBase, InputBaseProps, Menu, MenuItem, SvgIconProps, Theme, alpha } from '@mui/material';
import { AddRounded, ArrowDropDownRounded, RemoveRounded } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

/**
 * Light form controls for screens that draw dozens of them (lesson rules,
 * the bell schedule): a plain input, and a button that opens its menu only
 * when clicked, styled like the outlined fields elsewhere. A full MUI field
 * per value made those screens slow to open and to edit.
 */
export const fieldSx = (theme: Theme) => ({
  height: 36,
  width: '100%',
  px: 1.25,
  borderRadius: '8px',
  border: '1px solid',
  borderColor: alpha(theme.palette.text.primary, 0.23),
  bgcolor: 'background.paper',
  fontSize: '0.875rem',
  transition: 'border-color 120ms',
  '&:hover:not(.Mui-disabled)': { borderColor: 'text.primary' },
  '&.Mui-focused, &:focus-visible': { borderColor: 'primary.main', boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`, outline: 'none' },
  '&.Mui-disabled': { color: 'text.disabled', borderColor: 'divider' },
});

const COMMIT_DELAY_MS = 500;

/**
 * A count with − and + around it. Clicks settle for a moment before saving,
 * so tapping + three times is one save; typing saves when you leave. Out of
 * range, it says why and goes back.
 */
export const Stepper: React.FC<{
  value: number; min: number; max: number; disabled: boolean; label: string; rangeHint: string; onCommit: (v: number) => void;
}> = ({ value, min, max, disabled, label, rangeHint, onCommit }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState(String(value));
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => setText(String(value)), [value]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const commit = (raw: string) => {
    clearTimeout(timer.current);
    timer.current = undefined;
    const v = parseInt(raw, 10);
    if (Number.isNaN(v) || v < min || v > max) {
      if (raw.trim() !== String(value)) enqueueSnackbar(rangeHint, { variant: 'warning' });
      setText(String(value));
      return;
    }
    if (v !== value) onCommit(v);
  };
  // The count being stepped, read synchronously so quick clicks add up.
  const stepped = useRef(value);
  const step = (by: number) => {
    if (!timer.current) stepped.current = parseInt(text, 10) || value;
    const next = Math.min(max, Math.max(min, stepped.current + by));
    stepped.current = next;
    setText(String(next));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = undefined; commit(String(next)); }, COMMIT_DELAY_MS);
  };
  const current = parseInt(text, 10);
  const buttonSx = { width: 26, height: 26, borderRadius: '7px', color: 'text.secondary', '&:hover': { bgcolor: 'action.hover', color: 'text.primary' }, '&.Mui-disabled': { color: 'text.disabled' } };

  return (
    <Box sx={(theme) => ({ ...fieldSx(theme), display: 'inline-flex', alignItems: 'center', px: 0.25, gap: 0.25, width: '100%', maxWidth: 104 })}>
      <ButtonBase disableRipple disabled={disabled || current <= min} onClick={() => step(-1)} aria-label={`Fewer ${label.toLowerCase()}`} sx={buttonSx}>
        <RemoveRounded sx={{ fontSize: 16 }} />
      </ButtonBase>
      <InputBase
        value={text} disabled={disabled}
        onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={() => commit(text)}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        inputProps={{ inputMode: 'numeric', 'aria-label': label, style: { textAlign: 'center', padding: 0, fontWeight: 600 } }}
        sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem' }}
      />
      <ButtonBase disableRipple disabled={disabled || current >= max} onClick={() => step(1)} aria-label={`More ${label.toLowerCase()}`} sx={buttonSx}>
        <AddRounded sx={{ fontSize: 16 }} />
      </ButtonBase>
    </Box>
  );
};

export interface PickerOption<T> { value: T; label: string; disabled?: boolean; icon?: React.ReactElement<SvgIconProps> }

/**
 * A choice shown as text; its menu exists only while open. As a `pill`, the
 * first option is the quiet default and anything else is tinted, so what has
 * been customised stands out.
 */
export function RulePicker<T extends string | number | null>({ value, options, disabled, label, onChange, variant = 'field' }: {
  value: T; options: PickerOption<T>[]; disabled: boolean; label: string; onChange: (v: T) => void; variant?: 'field' | 'pill';
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const selected = options.find((o) => o.value === value);
  const isDefault = options[0]?.value === value;
  const pill = variant === 'pill';
  return (
    <>
      <ButtonBase
        disableRipple
        disabled={disabled}
        aria-label={label}
        aria-haspopup="listbox"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={(theme) => (pill
          ? {
            height: 30, maxWidth: '100%', px: 1.25, gap: 0.6, borderRadius: '999px', justifyContent: 'flex-start',
            fontSize: '0.8rem', fontWeight: isDefault ? 500 : 600,
            color: isDefault ? 'text.secondary' : 'primary.main',
            border: '1px solid', borderColor: isDefault ? 'transparent' : alpha(theme.palette.primary.main, 0.35),
            bgcolor: isDefault ? 'transparent' : alpha(theme.palette.primary.main, 0.08),
            transition: 'background-color 120ms, border-color 120ms',
            '&:hover:not(.Mui-disabled)': { bgcolor: isDefault ? 'action.hover' : alpha(theme.palette.primary.main, 0.14), borderColor: isDefault ? 'divider' : undefined },
            '&:focus-visible': { outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`, outlineOffset: 1 },
            '& .rp-icon': { fontSize: 16, opacity: isDefault ? 0.7 : 1 },
          }
          : { ...fieldSx(theme), justifyContent: 'space-between', gap: 0.5, pr: 0.5, textAlign: 'left' })}
      >
        {pill && selected?.icon && React.cloneElement(selected.icon, { className: 'rp-icon' })}
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected?.label ?? ''}</Box>
        {!pill && <ArrowDropDownRounded sx={{ color: 'text.secondary', flexShrink: 0, ml: 'auto' }} />}
      </ButtonBase>
      {anchor && (
        <Menu anchorEl={anchor} open onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { minWidth: anchor.offsetWidth, borderRadius: '10px' } } }}>
          {options.map((o) => (
            <MenuItem
              key={String(o.value)}
              selected={o.value === value}
              disabled={o.disabled}
              onClick={() => { setAnchor(null); if (o.value !== value) onChange(o.value); }}
              sx={{ gap: 1.25, fontSize: '0.875rem' }}
            >
              {o.icon && React.cloneElement(o.icon, { fontSize: 'small', sx: { color: 'text.secondary' } })}
              {o.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}

/** A plain outlined input (text, time…) in the same light style. */
export const LightInput: React.FC<InputBaseProps> = ({ sx, ...props }) => (
  <InputBase {...props} sx={[fieldSx, ...(Array.isArray(sx) ? sx : [sx])]} />
);
